// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FULFILLMENT_METHODS } from "@keller/XDL/constants";

export const getLogoImageWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields;
};

export const getMaintenanceMessageWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "MaintenanceMessage/MaintenanceMessage";
    })?.fields;
};

export const getHeaderSearchInputWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderSearchInput";
    })?.fields;
};

export const getMainNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/MainNavigation";
    })?.fields;
};

/**
 * isUserPurchasable means the user not webOnly
 * so getIsNonPurchasable = true => webOnly
 * @param state
 */
export const getIsNonPurchasable = (state: ApplicationState) => {
    return getSession(state).properties.isUserPurchasable?.toLowerCase() === "false";
};

export const getBillTrustLink = (state: ApplicationState): string | undefined => {
    return getSession(state).properties.billTrustLink;
};

export const getIsCreditHold = (properties?: { [key: string]: string }) => {
    return properties?.onCreditHold?.toLowerCase() === "true";
};

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
};

export const xisCheckoutDisabled = (cart: Cart | undefined) => {
    return !canCheckoutWithCart(cart) || hasOnlyQuoteRequiredProducts(cart) || isCartCheckoutDisabled(cart);
};

/**
 * Check Wether fulfillmentMethod is pickup or not as it can be pickup/counter
 * @param {String} fulfillmentMethod
 */

export const xIsPickup = (fulfillmentMethod: string) => {
    return [FULFILLMENT_METHODS.PickUp, FULFILLMENT_METHODS.Counter].includes(fulfillmentMethod.toLowerCase());
};

type UserRoleMatch = "any" | "all" | "none";

/**
 * Checks whether the authenticated user satisfies a set of role requirements.
 *
 * The function evaluates the user's roles (stored as a comma-separated string
 * in the session) against the provided roles array using the specified predicate.
 *
 *
 * @param state - The application state containing authentication and session data.
 * @param roles - A list of roles to evaluate against the user's assigned roles.
 * @param match - The role-matching strategy to apply can have values of `"any" | "all" | "none"`. Defaults to `"one-or-more"`.
 *
 * @returns `true` if the role condition is satisfied; otherwise, `false`.
 *
 */

export const xHasRoles = (state: ApplicationState, roles: string[], match: UserRoleMatch = "any") => {
    const isAuthenticated = xIsAuthenticated(state);
    if (!isAuthenticated) {
        return false;
    }

    const session = getSession(state);

    if (!session.userRoles) {
        return false;
    }

    const userRoles = session.userRoles
        .trim()
        .split(",")
        .map(r => r.trim());

    if (match === "any") {
        return roles.some(role => userRoles.includes(role.trim()));
    }

    if (match === "all") {
        return roles.every(role => userRoles.includes(role.trim()));
    }

    if (match === "none") {
        return roles.every(role => !userRoles.includes(role.trim()));
    }
};
